import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { NavigationError, NavigationStart, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { RouterOutlet } from '@angular/router';
import { partnerIdForOrg } from '@crewdle/white-label';
import { WhiteLabelService } from '@crewdle/white-label/angular';
import { SupportChatbotComponent } from './support-chatbot/support-chatbot.component';
import { DataStoreService } from './core/services/data-store.service';
import {
  CrwdAppUpdate, CrwdAppUpdateService, isChunkLoadError, reloadOnceForChunkError,
  registerChunkErrorRecovery,
} from '@crewdle/ui-kit';
import { firstNavigationSettled } from './core/boot-splash';
import { OrgContextService } from './core/services/org-context.service';
import { UserService } from './core/services/user.service';
import { ThemeService } from './core/theme.service';
import { LanguageService } from './core/translation.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SupportChatbotComponent, CrwdAppUpdate, TranslocoModule],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.css',
})
export class App {
  private dataStore = inject(DataStoreService);
  private appUpdate = inject(CrwdAppUpdateService);
  private router = inject(Router);
  private theme = inject(ThemeService);
  private language = inject(LanguageService);
  private orgContext = inject(OrgContextService);
  private userService = inject(UserService);
  // Instantiated app-wide so partner white-label (theme/favicon/title) applies on
  // every route, including the pages that don't render the sidebar.
  private whiteLabel = inject(WhiteLabelService);
  constructor() {
    // Recover from post-deploy chunk-load failures that happen outside the
    // router (dynamic imports, deferred views).
    registerChunkErrorRecovery();

    // Feed org changes to the white-label service. Only once an org is present:
    // org context resolves async after auth, and propagating the transient null
    // would clear the cached pre-paint brand mid-boot (a flash of Crewdle
    // defaults). Switching to a partner-less org still clears — the org is
    // non-null and partnerIdForOrg() returns null.
    effect(() => {
      const org = this.orgContext.currentOrg();
      if (org) this.whiteLabel.setPartnerId(partnerIdForOrg(org));
    });

    // Hold the index.html loader until branding is decided (partner or
    // Crewdle) so the chrome never flips brands in front of the user, AND
    // until the first page has rendered: arriving from the auth portal or a
    // mobile handoff, the auth guard is still exchanging the code and loading
    // the organizations, and removing the splash then showed a blank page.
    // Both waits are time-capped, so a slow read can't trap anyone here.
    void Promise.all([this.whiteLabel.settled(), this.router.navigated ? undefined : firstNavigationSettled(this.router.events)]).then(() => {
      document.getElementById('splash-screen')?.remove();
    });

    // Keep theme/language in sync with the user doc. The first snapshot
    // fires with the current Firestore value and supersedes the cached
    // localStorage/cookie preference; subsequent snapshots deliver
    // agent-driven and cross-tab changes live.
    effect(() => {
      const user = this.dataStore.authUser();
      if (user) {
        this.theme.startUserSync(user.uid);
        this.language.startUserSync(user.uid);
        // Ensure the user doc (incl. profilePictureUrl) is populated in
        // the store so the sidebar avatar is available on every page.
        this.userService.getCurrentUserDocument(true).catch(() => {});
      } else {
        this.theme.stopUserSync();
        this.language.stopUserSync();
        this.orgContext.stopCurrentOrgSync();
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart && this.appUpdate.updateAvailable()) {
        location.reload();
        return;
      }
      if (event instanceof NavigationError && isChunkLoadError(event.error)) {
        reloadOnceForChunkError();
      }
    });
  }
}
